import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { CityWordmark } from "@/components/CityBrand";
import { AsLogo } from "@/components/AsLogo";

const GROUPS = ["emergency", "utility", "civic"];

export function CityFooter() {
  const { t, lang, tenant } = useApp();
  const [contacts, setContacts] = useState([]);
  // R3-10: not-yet-fetched is not "no contacts" - hold a placeholder until the request settles.
  const [contactsLoaded, setContactsLoaded] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setContactsLoaded(false);
    api
      .get("/city/contacts", { params: { tenant_id: tenant.id } })
      .then((r) => setContacts(rows(r.data)))
      .catch(() => setContacts([]))
      .finally(() => setContactsLoaded(true));
  }, [tenant]);

  const cityName = localized(lang, tenant, "name");
  const links = [
    ["/directory", t("nav.directory")],
    ["/workers", t("nav.workers")],
    ["/services", t("services.navLabel")],
    ["/events", t("nav.events")],
    ["/jobs", t("nav.jobs")],
    ["/community", t("nav.community")],
  ];

  return (
    <footer className="border-t border-border bg-muted/30 px-5 py-14 sm:px-8" data-testid="city-footer">
      <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AsLogo size={36} className="text-[var(--as-primary)]" />
            <CityWordmark name={cityName} nameHi={tenant?.name_hi} className="text-lg" />
          </div>
          <p className="max-w-xs text-sm text-muted-foreground">{localized(lang, tenant, "tagline")}</p>
          {/* RF-21: the "City: <name>" mono line repeated the wordmark right above it. */}
        </div>

        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("footer.quickLinks")}
          </h3>
          <ul className="space-y-2 text-sm">
            {links.map(([to, label]) => (
              <li key={to}>
                <Link
                  to={to}
                  data-testid={`footer-link-${to.replace("/", "")}`}
                  className="text-muted-foreground transition-colors duration-200 hover:text-foreground"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-6 md:col-span-2">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("footer.contactsTitle")}
          </h3>
          {!contactsLoaded ? (
            <div className="h-5 w-48 animate-pulse rounded bg-muted" data-testid="footer-contacts-loading" aria-hidden="true" />
          ) : contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("footer.contactsEmpty")}</p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-3">
              {GROUPS.map((group) => {
                // Named for what it holds, so it cannot be read as the shared `rows()` helper
                // imported above — a different thing with the same name in one file.
                const groupRows = contacts.filter((c) => c.group === group);
                if (groupRows.length === 0) return null;
                return (
                  <div key={group} className="space-y-2" data-testid={`footer-contacts-${group}`}>
                    <p className="text-sm font-semibold">{t(`footer.${group}`)}</p>
                    <ul className="space-y-1.5">
                      {groupRows.map((c) => (
                        <li key={c.id}>
                          <a
                            href={`tel:${c.phone}`}
                            data-testid={`footer-contact-${c.id}`}
                            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--primary))]"
                          >
                            <Phone size={13} />
                            <span className="flex-1">{lang === "hi" ? c.label_hi || c.label : c.label}</span>
                            <span className="font-mono text-xs">{c.phone}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* D-09: Privacy & Terms — reachable from every page (a live privacy URL is a Play Store
        publish requirement). Both routes render PolicyPage. */}
      <div className="mx-auto mt-10 flex max-w-7xl flex-wrap items-center gap-x-5 gap-y-2 border-t border-border pt-6 text-sm">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t("footer.legal")}
        </span>
        <Link
          to="/privacy"
          data-testid="footer-link-privacy"
          className="text-muted-foreground transition-colors duration-200 hover:text-foreground"
        >
          {t("footer.privacy")}
        </Link>
        <Link
          to="/terms"
          data-testid="footer-link-terms"
          className="text-muted-foreground transition-colors duration-200 hover:text-foreground"
        >
          {t("footer.terms")}
        </Link>
      </div>
    </footer>
  );
}
